import React, { useContext, useEffect, useMemo, useState } from "react";
import { eachDeep } from "deepdash-es/standalone";
import { useTranslation } from "react-i18next";
import { MenuList, MenuItem, Paper } from "@mui/material";
import ContextMenu from "../ContextMenu/ContextMenu";
import { useDispatch, useSelector } from "react-redux";
import NewRecord from "./ContextActions/NewRecord";
import NewField from "./ContextActions/NewField";
import Delete from "./ContextActions/Delete";
import CopyAndPaste, { getSelectedNodes } from "./ContextActions/CopyAndPaste";
import NewComment from "./ContextActions/NewComment";
import { DATA_TYPE } from "../../constants/datatypes";
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import NewVariable from "./ContextActions/NewVariable";
import NewVariableCollection from "./ContextActions/NewVariableCollection";
import NewSequence from "./ContextActions/NewSequence";
import QuickAddModal from "./ContextActions/QuickAddModal";
import NewCustomDBField from "./ContextActions/NewCustomDBField";
import NewPlugin from "./ContextActions/NewPlugin";
import NewAction from "./ContextActions/NewAction";
import NewConditionalIfField from "./ContextActions/NewConditionalFieldIF";
import NewConditionalELSEField from "./ContextActions/NewConditionalFieldELSE";
import NewTrackingReport from "./ContextActions/NewTrackingReport";
import NewFieldOutbound from "./ContextActions/NewFieldOutbound";
import { cloneDeep } from "lodash";
import { treeActions } from "../../redux/slices/treeSlice";
import NewFieldGroup from "./ContextActions/NewFieldGroup";
import NewRecordOutbound from "./ContextActions/NewRecordOutbound";
import { isParentFieldGroup } from "../../utils/utilFunctions";

const MODALS = {
  MNU_NEW_RECORDS: "MNU_NEW_RECORDS",
  MNU_NEW_FLD_GRP: "MNU_NEW_FLD_GRP",
  MNU_NEW_VAR: "MNU_NEW_VAR",
  MNU_NEW_FIELD: "MNU_NEW_FIELD",
  MNU_NEW_VAR_COLL: "MNU_NEW_VAR_COLL",
  MNU_NEW_SEQ: "MNU_NEW_SEQ",
  MNU_NEW_CUST_DB_FLD: "MNU_NEW_CUST_DB_FLD",
  MNU_NEW_PLUG: "MNU_NEW_PLUG",
  MNU_NEW_COND_FLD_LIST: "MNU_NEW_COND_FLD_LIST",
  MNU_NEW_COND_FLD_LIST_ELSE: "MNU_NEW_COND_FLD_LIST_ELSE",
  BTN_NEW_TRACKING_REPORT: "BTN_NEW_TRACKING_REPORT",
  MNU_QUICK_FIELD_ADD: "MNU_QUICK_FIELD_ADD",
  MNU_COPIED: 'MNU_COPIED',
  DELETE: 'DELETE',
  MNU_NEW_COMMENT: 'MNU_NEW_COMMENT',
  AI_AUTO_MAP: 'AI_AUTO_MAP',
  AI_THUMBS_UP: 'AI_THUMBS_UP',
  AI_THUMBS_DOWN: 'AI_THUMBS_DOWN'
};

function OutboundJSONMenu(props) {
  const { t } = useTranslation();
  const [showContent, setContext] = useState(false);
  const [modalToOpen, setModalToOpen] = useState(null);
  // const [isCopied, setIsCopied] = useState({ copy: false, paste: false });
  const [isDisable, setIsDisable] = useState(false);
  const [showElse, setShowElse] = useState(false);
  const dispatch = useDispatch();
  const parentScreen = useContext(ParentScreenCtx);
  const type = useSelector(
    (state) =>
      state.treeData[parentScreen].leftTree.tree.body.selectedNode?.type
  );
  const selectedNode = useSelector(
    (state) => state.treeData[parentScreen].rightTree.tree.body.selectedNode
  );
  const { tree, currentTab } = useSelector(
    (state) => state.treeData[parentScreen].rightTree
  );
  const { rightTree, leftTree } = useSelector(
    (state) => state.treeData[parentScreen]
  );
  const translatedData = tree[currentTab].translatedData;
  const templateHeader = tree.templateHeader;
  const selectedKeysArray = tree[currentTab].selectedKeysArray;
  const handleClick = (contextItem) => {
    if (contextItem === MODALS.AI_AUTO_MAP) {
      aiAutoMapFields();
    } else if (contextItem === MODALS.AI_THUMBS_UP) {
      handleAIAction(true);
    } else if (contextItem === MODALS.AI_THUMBS_DOWN) {
      handleAIAction(false);
    } else {
      setModalToOpen(contextItem);
    }
  };
  const { isCopied, setIsCopied } = props;

  const unmapField = () => {
    const keysToUnmap = [
      ...new Set(
        [...selectedKeysArray, selectedNode?.key].filter(
          (key) => (key !== "ob-0" || key !== "obh-0" || key !== "obt-0")
        )
      ),
    ];
    if (!Array.isArray(keysToUnmap) || keysToUnmap.length === 0) return;
    const newTree = cloneDeep(tree[currentTab].translatedData);
    let prevSelectedNode = null;

    eachDeep(newTree, (obj) => {
      if (obj?.key && keysToUnmap.includes(obj.key)) {
        obj.ICON_DISPLAY = "FIELD";

        // Remove specific properties
        [
          "PARENT",
          "SOURCE_TYPE",
          "RECORD_TYPE",
          "MAPNAME",
          "AI_CONFIDENCE",
          "ACTION",
          "INDEX",
          "MAP_REPETITION_INDEX",
          "SOURCE_REPEATING_FIELD"
        ].forEach((e) => {
          if (obj.hasOwnProperty(e)) delete obj[e];
        });

        // Store the previous selected node if it matches
        if (obj.key === tree[currentTab]?.selectedNode.key) {
          prevSelectedNode = obj;
        }
      }
    });

    dispatch(
      treeActions.setNewRightTreeTranslatedData({
        screen: parentScreen,
        type: currentTab,
        data: newTree,
      })
    );

    if (prevSelectedNode) {
      dispatch(
        treeActions.setRightTreeSelectedNode({
          screen: parentScreen,
          type: currentTab,
          data: prevSelectedNode,
        })
      );
    }
  };

  const aiAutoMapFields = () => {
    const keysToMap = [...new Set([...selectedKeysArray, selectedNode?.key].filter(key => (key !== 'ob-0' || key !== 'obh-0' || key !== 'obt-0')))];
    if (!Array.isArray(keysToMap) || keysToMap.length === 0) return;
    const newTree = cloneDeep(tree[currentTab].translatedData);
    let prevSelectedNode = null;

    eachDeep(newTree, (obj) => {
      if (obj?.key && keysToMap.includes(obj.key) && obj.ICON_DISPLAY === "FIELD") {
        obj.AI_AUTOMAP = "TRUE";

        // Store the previous selected node if it matches
        if (obj.key === tree[currentTab]?.selectedNode.key) {
          prevSelectedNode = obj;
        }
      }
    });

    dispatch(treeActions.setNewRightTreeTranslatedData({
      screen: parentScreen,
      type: currentTab,
      data: newTree
    }));

    if (prevSelectedNode) {
      dispatch(treeActions.setRightTreeSelectedNode({
        screen: parentScreen,
        type: currentTab,
        data: prevSelectedNode,
      }));
    }

    // Trigger AI Auto Map functionality after saving the fields
    dispatch(treeActions.setTriggerAiAutoMap({ triggered: true, fromCsvMenu: true }));
  };

  const handleAIAction = (isThumbUpButtonClicked) => {
    const keysToProcess = [...new Set([...selectedKeysArray, selectedNode?.key].filter(key => (key !== 'ob-0' || key !== 'obh-0' || key !== 'obt-0')))];
    if (!Array.isArray(keysToProcess) || keysToProcess.length === 0) return;
    
    const newTree = cloneDeep(tree[currentTab].translatedData);
    let prevSelectedNode = null;

    eachDeep(newTree, (obj) => {
      if (obj?.key && keysToProcess.includes(obj.key) && obj.hasOwnProperty("AI_CONFIDENCE")) {
        if (isThumbUpButtonClicked) {
          if (obj.hasOwnProperty("AI_CONFIDENCE"))
            delete obj.AI_CONFIDENCE;
          obj.AI_USER_CONFIRMED = "TRUE";
        } else {
          [
            "AI_CONFIDENCE",
            "MAPNAME",
            "PARENT",
            "SOURCE_TYPE",
            "RECORD_TYPE",
            "AI_USER_CONFIRMED"
          ].forEach((e) => {
            if (obj.hasOwnProperty(e)) delete obj[e];
          });
        }

        // Store the previous selected node if it matches
        if (obj.key === tree[currentTab]?.selectedNode.key) {
          prevSelectedNode = obj;
        }
      }
    });

    dispatch(treeActions.setNewRightTreeTranslatedData({
      screen: parentScreen,
      type: currentTab,
      data: newTree
    }));

    if (prevSelectedNode) {
      dispatch(treeActions.setRightTreeSelectedNode({
        screen: parentScreen,
        type: currentTab,
        data: prevSelectedNode,
      }));
    }
  };

  const isOnlyFieldsSelected = useMemo(() => {
    if (!selectedNode || !selectedKeysArray.length) return false;
    
    const allKeys = [...new Set([...selectedKeysArray, selectedNode?.key])];
    let isAllFields = true;

    eachDeep(tree[currentTab]?.translatedData, (obj) => {
      if (obj?.key && allKeys.includes(obj.key)) {
        if (!["FIELD", "DATABASE_FIELD", "ATTRIBUTE"].includes(obj.ICON_DISPLAY)) {
          isAllFields = false;
          return false; // break out of eachDeep
        }
      }
    });

    return isAllFields;
  }, [selectedKeysArray, translatedData]);

  const isOnlyAIFieldsSelected = useMemo(() => {
    if (!selectedNode || !selectedKeysArray.length) return false;
    
    const allKeys = [...new Set([...selectedKeysArray, selectedNode?.key])];
    let isAllFields = true;
    let foundAIField = false;
    eachDeep(tree[currentTab]?.translatedData, (obj) => {
      if (obj?.key && allKeys.includes(obj.key)) {
        if (!["FIELD", "DATABASE_FIELD", "ATTRIBUTE"].includes(obj.ICON_DISPLAY)) {
          isAllFields = false;
          return false; // break out of eachDeep
        }
        if (obj.hasOwnProperty("AI_CONFIDENCE")) {
          foundAIField = true;
        }
      }
    });

    return isAllFields && foundAIField;
  }, [selectedKeysArray, translatedData]);

  const areSelectedFieldsUnmapped = useMemo(() => {
    if (!selectedNode || selectedKeysArray.length === 0) return false;
    
    const allKeys = [...new Set([...selectedKeysArray, selectedNode?.key])];
    let hasUnmappedFields = false;

    eachDeep(tree[currentTab]?.translatedData, (obj) => {
      if (obj?.key && allKeys.includes(obj.key) && ["FIELD", "DATABASE_FIELD", "ATTRIBUTE"].includes(obj.ICON_DISPLAY) && obj?.FIELDTYPE !== "CONSTANT") {
        // Check if field is unmapped (same logic as OutboundTemplateTree)
        if (!obj.hasOwnProperty("RECORD_TYPE") || 
            !obj.hasOwnProperty("MAPNAME") || 
            obj.MAPNAME === t("SELECT")) {
          hasUnmappedFields = true;
          return false; // break out of eachDeep when we find an unmapped field
        }
      }
    });

    return hasUnmappedFields;
  },[selectedNode, selectedKeysArray, tree[currentTab]?.translatedData]);

  const showMenuOrNot = useMemo(() => {
    //Checking if template is Empty or Not.
    const isTemplateEmpty =
      currentTab === "body" && parentScreen === "inbound"
        ? tree[currentTab]?.translatedData[0]?.children?.length !== 0 &&
          tree[currentTab].selectedNode?.ICON_DISPLAY !== "TEMPLATE_BODY_DOTS"
        : tree[currentTab]?.translatedData?.length !== 0;

    //Checking if node is Selected or not.
    const nodeSelected =
      Object.keys(tree[currentTab]?.selectedNode || {}).length > 0;
    return isTemplateEmpty && nodeSelected;
    
  }, [tree[currentTab]?.translatedData, tree[currentTab].selectedNode, tree]);

  const checkDisable = () => {
    return (
      selectedKeysArray.includes("ib-0") &&
      currentTab === "body" &&
      parentScreen === "inbound"
    );
  };

  useEffect(() => {
    setIsDisable(checkDisable());
  }, [selectedKeysArray]);

  useEffect(() => {
    if (selectedNode.ICON_DISPLAY === "CONDITION_IF") {
      let found = false;
      eachDeep(tree[currentTab]?.translatedData, (obj) => {
        if (
          obj?.nodeId === selectedNode.nodeId &&
          obj?.ICON_DISPLAY === "CONDITION_ELSE"
        ) {
          setShowElse(false);
          found = true;
          return;
        }
      });
      if (!found) {
        setShowElse(true);
      }
    } else {
      const splitedKey = selectedNode?.key?.split("-") || [];
      splitedKey.pop();
      let parentKey = splitedKey.join("-");
      const parentNodes = getSelectedNodes([parentKey], tree[currentTab]?.translatedData, true);
      const hasConditionIfParent = parentNodes.some(node => node.ICON_DISPLAY === "CONDITION_IF");
      if (hasConditionIfParent && parentNodes.length === 1) {
        setShowElse(true);
      }else if (hasConditionIfParent && parentNodes.length > 1) {
        setShowElse(false);
      }else {
        setShowElse(false);
      }
    }
  }, [selectedNode?.key]);

  return (
    <>
      {type === "outboundTemplate" &&
      showMenuOrNot &&
      !Array.isArray(templateHeader) &&
      DATA_TYPE.hasOwnProperty(templateHeader.FILETYPE) &&
      templateHeader?.FILETYPE === "JSON" ? (
        <ContextMenu targetId={props.targetId}>
          <Paper elevation={3} sx={{ width: "19rem" }}>
            <MenuList sx={{ paddingTop: 0, paddingBottom: 0 }}>
              {(isOnlyFieldsSelected && !selectedNode?.AI_CONFIDENCE && areSelectedFieldsUnmapped) &&
                  <MenuItem name={t("AI_AUTO_MAP") || "AI Auto Map"}
                    onClick={() => handleClick(MODALS.AI_AUTO_MAP)}
                  >
                    <span className={"AI_GREY"} style={{width: "17px"}}></span>
                    <span className={"margin-left-8"}>{t("AI_AUTO_MAP") || "AI Auto Map"}</span>
                  </MenuItem>
                }
              {isOnlyAIFieldsSelected &&(
                <MenuItem 
                  name={t("AI_THUMBS_UP") || "AI Thumbs Up"}
                  onClick={() => handleClick(MODALS.AI_THUMBS_UP)}>
                  <span className={"THUMB_UP_MENU"}></span>
                  <span className={"margin-left-8"}>{t("AI_THUMBS_UP") || "AI Thumbs Up"}</span>
                </MenuItem>
              )}
              {isOnlyAIFieldsSelected && (
                <MenuItem 
                  name={t("AI_THUMBS_DOWN") || "AI Thumbs Down"}
                  onClick={() => handleClick(MODALS.AI_THUMBS_DOWN)}>
                  <span className={"THUMB_DOWN_MENU"}></span>
                  <span className={"margin-left-8"}>{t("AI_THUMBS_DOWN") || "AI Thumbs Down"}</span>
                </MenuItem>
              )}
                <MenuItem
                  name={t("MNU_NEW_OBJECT")}
                  style={
                    selectedNode?.ICON_DISPLAY === "FIELD_GROUP" &&
                    isParentFieldGroup(translatedData, selectedNode)
                      ? { display: "none" }
                      : {}
                  }
                  onClick={() => handleClick(MODALS.MNU_NEW_RECORDS)}>
                  <span className={"RECORD"}></span>
                  <span className={"margin-left-8"}>{t("MNU_NEW_OBJECT")}</span>
                </MenuItem>
              <MenuItem
                name={t("MNU_NEW_FLD")}
                onClick={() => handleClick(MODALS.MNU_NEW_FIELD)}>
                <span className={"FIELD"}></span>
                <span className={"margin-left-8"}>{t("MNU_NEW_FLD")}</span>
              </MenuItem>

              <MenuItem
                name={t("MNU_QUICK_FIELD_ADD")}
                onClick={() => handleClick(MODALS.MNU_QUICK_FIELD_ADD)}>
                <span className={"QUICK_ADD_FIELDS"}></span>
                <span className={"margin-left-8"}>
                  {t("MNU_QUICK_FIELD_ADD")}
                </span>
              </MenuItem>
              <MenuItem
                name={t("MNU_NEW_COND_FLD_LIST")}
                onClick={() =>
                  handleClick(MODALS.MNU_NEW_COND_FLD_LIST)
                }>
                <span className={"CONDITION_IF"}></span>
                <span className={"margin-left-8"}>
                  {t("MNU_NEW_COND_FLD_LIST")}
                </span>
              </MenuItem>

              {((tree[currentTab]?.selectedNode.ICON_DISPLAY === "CONDITION_IF" &&
                showElse) || (showElse)) && (
                  <MenuItem
                    name={t("MNU_NEW_COND_FLD_LIST_ELSE")}
                    onClick={() =>
                      handleClick(MODALS.MNU_NEW_COND_FLD_LIST_ELSE)
                    }>
                    <span className={"CONDITION_ELSE"}></span>
                    <span className={"margin-left-8"}>
                      {t("MNU_NEW_COND_FLD_LIST_ELSE")}
                    </span>
                  </MenuItem>
                )}
              <MenuItem
                name={t("MNU_NEW_VAR")}
                onClick={() => handleClick(MODALS.MNU_NEW_VAR)}>
                <span className={"VARIABLE"}></span>
                <span className={"margin-left-8"}>{t("MNU_NEW_VAR")}</span>
              </MenuItem>
              <MenuItem
                name={t("MNU_NEW_VAR_COLL")}
                onClick={() => handleClick(MODALS.MNU_NEW_VAR_COLL)}>
                <span className={"VARIABLE_COLLECTION"}></span>
                <span className={"margin-left-8"}>{t("MNU_NEW_VAR_COLL")}</span>
              </MenuItem>
              <MenuItem
                name={t("MNU_NEW_SEQ")}
                onClick={() => handleClick(MODALS.MNU_NEW_SEQ)}>
                <span className={"SEQUENCE"}></span>
                <span className={"margin-left-8"}>{t("MNU_NEW_SEQ")}</span>
              </MenuItem>
              <MenuItem
                name={t("MNU_NEW_CUST_DB_FLD")}
                onClick={() => handleClick(MODALS.MNU_NEW_CUST_DB_FLD)}>
                <span className={"CUSTOM_FIELD"}></span>
                <span className={"margin-left-8"}>
                  {t("MNU_NEW_CUST_DB_FLD")}
                </span>
              </MenuItem>
              <MenuItem
                name={t("MNU_NEW_PLUG")}
                onClick={() => handleClick(MODALS.MNU_NEW_PLUG)}>
                <span className={"PLUGIN"}></span>
                <span className={"margin-left-8"}>{t("MNU_NEW_PLUG")}</span>
              </MenuItem>
              <MenuItem
                name={t("MNU_NEW_ACTION")}
                onClick={() => handleClick(MODALS.MNU_NEW_ACTION)}>
                <span className={"ACTION_ITEM"}></span>
                <span className={"margin-left-8"}>{t("MNU_NEW_ACTION")}</span>
              </MenuItem>
              <MenuItem
                name={t("MNU_USE_COMMENT")}
                onClick={() => handleClick(MODALS.MNU_NEW_COMMENT)}>
                <span className={"COMMENT"}></span>
                <span className={"margin-left-8"}>{t("MNU_USE_COMMENT")}</span>
              </MenuItem>
              <MenuItem
                name={t("BTN_NEW_TRACKING_REPORT")}
                onClick={() =>
                  handleClick(MODALS.BTN_NEW_TRACKING_REPORT)
                }>
                <span className={"TRACKING_REPORT_ITEM"}></span>
                <span className={"margin-left-8"}>
                  {t("BTN_NEW_TRACKING_REPORT")}
                </span>
              </MenuItem>
              <MenuItem
                disabled={isDisable}
                name={t("DELETE")}
                onClick={() => handleClick(MODALS.DELETE)}>
                <span className={"SYMBOL_DELETE"}></span>
                <span className={"margin-left-8"}>{t("DELETE")}</span>
              </MenuItem>
              <MenuItem
                disabled={isDisable || selectedNode?.ICON_DISPLAY === 'CONDITION_ELSE'}
                name={t("MNU_COPY")}
                onClick={() =>
                  setIsCopied({
                    paste: false,
                    copy: true,
                    dispatchFrom: "CsvMenu",
                    treeType: parentScreen,
                  })
                }>
                <span className={"COPY"}></span>
                <span className={"margin-left-8"}>{t("MNU_COPY")}</span>
              </MenuItem>
              {(isCopied?.copiedNodes?.length && (
                <MenuItem
                  name={t("MNU_PASTE")}
                  onClick={() =>
                    setIsCopied({
                      ...isCopied,
                      paste: true,
                      dispatchFrom: "CsvMenu",
                      treeType: parentScreen,
                    })
                  }>
                  <span className={"DOCUMENT"}></span>
                  <span className={"margin-left-8"}>{t("MNU_PASTE")}</span>
                </MenuItem>
              )) ||
                ""}
              {isOnlyFieldsSelected && (
                <MenuItem name={t("MNU_UNMAP")} onClick={unmapField}>
                  <span className={"WARNING"}></span>
                  <span className={"margin-left-8"}>{t("MNU_UNMAP")}</span>
                </MenuItem>
              )}
            </MenuList>
          </Paper>
        </ContextMenu>
      ) : (
        ""
      )}
      <NewRecordOutbound
        openModal={modalToOpen === MODALS.MNU_NEW_RECORDS}
        toggle={handleClick}
      />
      <NewFieldOutbound
        openModal={modalToOpen === MODALS.MNU_NEW_FIELD}
        toggle={handleClick}
      />

      <NewFieldGroup
        openModal={modalToOpen === MODALS.MNU_NEW_FLD_GRP}
        toggle={handleClick}
      />
      <Delete
        openModal={modalToOpen === MODALS.DELETE}
        toggle={() => handleClick(null)}
        type={type}></Delete>
      <NewComment
        openModal={modalToOpen === MODALS.MNU_NEW_COMMENT}
        toggle={() => handleClick(null)}></NewComment>
      <CopyAndPaste
        isCopied={isCopied}
        setIsCopied={setIsCopied}
        contextMenuType={"CsvMenu"}
        treeType={parentScreen}
      />
      <NewVariable
        openModal={modalToOpen === MODALS.MNU_NEW_VAR}
        toggle={handleClick}
      />
      <NewVariableCollection
        openModal={modalToOpen === MODALS.MNU_NEW_VAR_COLL}
        toggle={handleClick}
      />
      <NewSequence
        openModal={modalToOpen === MODALS.MNU_NEW_SEQ}
        toggle={handleClick}
      />
      <NewCustomDBField
        openModal={modalToOpen === MODALS.MNU_NEW_CUST_DB_FLD}
        toggle={handleClick}
      />
      <NewPlugin
        openModal={modalToOpen === MODALS.MNU_NEW_PLUG}
        toggle={handleClick}
      />
      <NewAction
        openModal={modalToOpen === MODALS.MNU_NEW_ACTION}
        toggle={handleClick}
      />
      <NewConditionalIfField
        openModal={modalToOpen === MODALS.MNU_NEW_COND_FLD_LIST}
        toggle={handleClick}
      />
      <NewConditionalELSEField
        openModal={modalToOpen === MODALS.MNU_NEW_COND_FLD_LIST_ELSE}
        toggle={handleClick}
      />
      <NewTrackingReport
        openModal={modalToOpen === MODALS.BTN_NEW_TRACKING_REPORT}
        toggle={handleClick}
      />
      <QuickAddModal
        openModal={modalToOpen === MODALS.MNU_QUICK_FIELD_ADD}
        toggle={() => handleClick(null)}></QuickAddModal>
    </>
  );
}
export default OutboundJSONMenu;
